import React, { useEffect, useRef, useState } from 'react'
import { Canvas, IText, FabricImage } from 'fabric'
import { HexColorPicker } from 'react-colorful'

const TextTools = ({ image, onTextComplete }) => {
  const canvasRef = useRef(null)
  const fabricCanvasRef = useRef(null)
  const [canvas, setCanvas] = useState(null)
  const [textValue, setTextValue] = useState('')
  const [fontSize, setFontSize] = useState(32)
  const [textColor, setTextColor] = useState('#000000')
  const [fontFamily, setFontFamily] = useState('Arial')
  const [fontWeight, setFontWeight] = useState('normal')
  const [textAlign, setTextAlign] = useState('left')
  const [showColorPicker, setShowColorPicker] = useState(false)
  const [backgroundColor, setBackgroundColor] = useState('transparent')
  const [showBgColorPicker, setShowBgColorPicker] = useState(false)

  useEffect(() => {
    if (!canvasRef.current) return

    const fabricCanvas = new Canvas(canvasRef.current, {
      backgroundColor: 'white'
    })

    fabricCanvasRef.current = fabricCanvas
    setCanvas(fabricCanvas)

    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const maxWidth = 1400
      const maxHeight = 1000
      let width = img.width
      let height = img.height

      if (width > maxWidth) {
        height = (height * maxWidth) / width
        width = maxWidth
      }
      if (height > maxHeight) {
        width = (width * maxHeight) / height
        height = maxHeight
      }

      fabricCanvas.setWidth(width)
      fabricCanvas.setHeight(height)

      FabricImage.fromURL(image, { crossOrigin: 'anonymous' }).then((fabricImg) => {
        fabricImg.scaleToWidth(width)
        fabricImg.scaleToHeight(height)
        fabricImg.selectable = false
        fabricCanvas.backgroundImage = fabricImg
        fabricCanvas.renderAll()
      })
    }
    img.src = image

    return () => {
      fabricCanvas.dispose()
    }
  }, [image])

  const addText = () => {
    if (!canvas || !textValue.trim()) return

    const text = new IText(textValue, {
      left: canvas.width / 2,
      top: canvas.height / 2,
      fontSize: fontSize,
      fill: textColor,
      fontFamily: fontFamily,
      fontWeight: fontWeight,
      textAlign: textAlign,
      backgroundColor: backgroundColor === 'transparent' ? '' : backgroundColor,
      originX: 'center',
      originY: 'center'
    })

    canvas.add(text)
    canvas.setActiveObject(text)
    canvas.renderAll()
    setTextValue('')
  }

  const handleDelete = () => {
    if (!canvas) return
    const activeObject = canvas.getActiveObject()
    if (activeObject) {
      canvas.remove(activeObject)
      canvas.renderAll()
    }
  }

  const handleClear = () => {
    if (!canvas) return
    canvas.getObjects().forEach((obj) => {
      if (obj !== canvas.backgroundImage) {
        canvas.remove(obj)
      }
    })
    canvas.renderAll()
  }

  const handleApply = () => {
    if (!canvas) return
    const dataURL = canvas.toDataURL({
      format: 'png',
      quality: 1
    })
    onTextComplete(dataURL)
  }

  const fonts = [
    'Arial',
    'Helvetica',
    'Times New Roman',
    'Courier New',
    'Verdana',
    'Georgia',
    'Palatino',
    'Garamond',
    'Comic Sans MS',
    'Impact'
  ]

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Text
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={textValue}
              onChange={(e) => setTextValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addText()}
              placeholder="Enter text..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={addText}
              disabled={!textValue.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              Add
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Font Family
          </label>
          <select
            value={fontFamily}
            onChange={(e) => setFontFamily(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {fonts.map(font => (
              <option key={font} value={font}>{font}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Font Size: {fontSize}px
          </label>
          <input
            type="range"
            min="12"
            max="120"
            value={fontSize}
            onChange={(e) => setFontSize(Number(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Font Weight
          </label>
          <div className="flex gap-2">
            <button
              onClick={() => setFontWeight('normal')}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                fontWeight === 'normal'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Normal
            </button>
            <button
              onClick={() => setFontWeight('bold')}
              className={`flex-1 px-4 py-2 rounded-lg font-bold transition-colors ${
                fontWeight === 'bold'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Bold
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Text Alignment
          </label>
          <div className="flex gap-2">
            {['left', 'center', 'right'].map(align => (
              <button
                key={align}
                onClick={() => setTextAlign(align)}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors capitalize ${
                  textAlign === align
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {align}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-2">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Text Color
            </label>
            <div className="relative">
              <button
                onClick={() => setShowColorPicker(!showColorPicker)}
                className="w-full flex items-center gap-2 px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                <div
                  className="w-6 h-6 rounded border-2 border-gray-400"
                  style={{ backgroundColor: textColor }}
                />
                <span className="font-medium text-gray-700">{textColor}</span>
              </button>
              {showColorPicker && (
                <div className="absolute z-10 mt-2">
                  <div
                    className="fixed inset-0"
                    onClick={() => setShowColorPicker(false)}
                  />
                  <div className="relative bg-white p-3 rounded-lg shadow-lg">
                    <HexColorPicker color={textColor} onChange={setTextColor} />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Background
            </label>
            <div className="relative">
              <button
                onClick={() => setShowBgColorPicker(!showBgColorPicker)}
                className="w-full flex items-center gap-2 px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                <div
                  className="w-6 h-6 rounded border-2 border-gray-400"
                  style={{
                    backgroundColor: backgroundColor === 'transparent' ? 'white' : backgroundColor,
                    backgroundImage: backgroundColor === 'transparent'
                      ? 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc)'
                      : 'none',
                    backgroundSize: backgroundColor === 'transparent' ? '10px 10px' : 'auto',
                    backgroundPosition: backgroundColor === 'transparent' ? '0 0, 5px 5px' : '0 0'
                  }}
                />
                <span className="font-medium text-gray-700 text-xs">
                  {backgroundColor === 'transparent' ? 'None' : backgroundColor}
                </span>
              </button>
              {showBgColorPicker && (
                <div className="absolute z-10 mt-2 right-0">
                  <div
                    className="fixed inset-0"
                    onClick={() => setShowBgColorPicker(false)}
                  />
                  <div className="relative bg-white p-3 rounded-lg shadow-lg">
                    <button
                      onClick={() => {
                        setBackgroundColor('transparent')
                        setShowBgColorPicker(false)
                      }}
                      className="w-full mb-2 px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 text-sm"
                    >
                      Transparent
                    </button>
                    <HexColorPicker
                      color={backgroundColor === 'transparent' ? '#ffffff' : backgroundColor}
                      onChange={setBackgroundColor}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-center bg-gray-100 rounded-lg p-4">
        <canvas ref={canvasRef} className="border border-gray-300 rounded" />
      </div>

      <div className="flex gap-2 justify-center">
        <button
          onClick={handleDelete}
          className="px-4 py-2 bg-yellow-600 text-white rounded-lg font-medium hover:bg-yellow-700 transition-colors"
        >
          Delete Selected
        </button>
        <button
          onClick={handleClear}
          className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
        >
          Clear All
        </button>
        <button
          onClick={handleApply}
          className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
        >
          Apply Text
        </button>
      </div>
    </div>
  )
}

export default TextTools
